import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import apiClient from './lib/api-client';
import { auth } from './lib/firebase';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isGuest, setIsGuest] = useState(true);
  const [userTier, setUserTier] = useState('guest');
  const [userData, setUserData] = useState(null);
  const [checkingAuth, setCheckingAuth] = useState(true);

  const setGuestMode = useCallback(() => {
    setIsAuthenticated(false);
    setIsGuest(true);
    setUserData(null);
    setUserTier('guest');
    localStorage.removeItem('user_tier');
  }, []);

  const mapTier = (subscription_level) => {
    if (!subscription_level) return 'guest';
    if (subscription_level==='pro') return 'pro';
    return 'signup' // signup+free version
  }

  const signOut = useCallback(async () => {
    try {
      await auth.signOut();
      await apiClient.logout();
    } catch (_) {}
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user_id');
    localStorage.removeItem('user_tier');
    setGuestMode();
    window.location.href = '/auth';
  }, [setGuestMode]);

  const runAuthCheck = useCallback(async () => {
    const token = localStorage.getItem('access_token');
    // No token at all → guest mode immediately, no redirect
    if (!token) {
      setGuestMode();
      setCheckingAuth(false);
      return;
    }

    try {
      setCheckingAuth(true);
      const authStatus = await apiClient.isAuthenticated();

      if (!authStatus.is_authenticated) {
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('user_tier');
        setGuestMode();
        return;
      }

      // If authenticated but profile not complete → redirect
      if (!authStatus.is_info) {
        // Only redirect if not already on the user-details page to avoid infinite loops
        if (window.location.pathname !== '/user/user-details') {
          window.location.href = '/user/user-details';
        }
        setCheckingAuth(false);
        return;
      }

      // If not verified → redirect
      if (!authStatus.is_verified) {
        if (window.location.pathname !== '/pending-verification') {
          window.location.href = '/pending-verification';
        }
        setCheckingAuth(false);
        return;
      }

      if (authStatus.user_id) {
        localStorage.setItem('user_id', authStatus.user_id);
      }

      const resolvedTier = mapTier(
        authStatus.subscription_level || authStatus.user_tier || localStorage.getItem('user_tier')
      );
      setUserTier(resolvedTier);
      localStorage.setItem('user_tier', resolvedTier);

      setUserData(authStatus);
      setIsAuthenticated(true);
      setIsGuest(false);

      // Fetch full profile from /my_details to populate userData with first_name, last_name, email etc.
      try {
        const profile = await apiClient.getUserDetails();
        setUserData(profile);
        
        if (profile && profile.subscription_level) {
          const newTier = mapTier(profile.subscription_level);
          setUserTier(newTier);
          localStorage.setItem('user_tier', newTier);
        }
      } catch (profileErr) {
        console.error('AuthContext: failed to fetch user profile', profileErr);
        // Keep authStatus as fallback so auth state is still valid
      }

    } catch (error) {
      console.error('AuthContext: auth check failed', error);
      if (error?.status === 401 || error?.status === 403) {
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('user_tier');
      }
      setGuestMode();
    } finally {
      setCheckingAuth(false);
    }
  }, [setGuestMode]);

  useEffect(() => {
    const storedTier = localStorage.getItem('user_tier');
    if (storedTier){
      setUserTier(storedTier);
    }
    runAuthCheck();

    // Listen for storage changes (for same-window login/logout)
    window.addEventListener('storage', runAuthCheck);

    // Periodic re-validation every 15 minutes (only for authenticated users)
    const interval = setInterval(async () => {
      const token = localStorage.getItem('access_token');
      if (!token) return;
      try {
        const authStatus = await apiClient.isAuthenticated();
        if (!authStatus.is_authenticated) {
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
          localStorage.removeItem('user_tier');
          setGuestMode();
          window.location.href = '/auth';
        }
      } catch (_) {}
    }, 15 * 60 * 1000);

    return () => {
      clearInterval(interval);
      window.removeEventListener('storage', runAuthCheck);
    };
  }, [runAuthCheck]);

  return (
    <AuthContext.Provider value={{ isAuthenticated, isGuest, userData, checkingAuth, signOut, checkAuth: runAuthCheck, userTier, setUserTier }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
};

export default AuthContext;
